import { Adjustments } from "@/core/types"
import { hashObject } from "@/utils/hash"
import { clamp } from "@/utils/math"
import { vec3, vec4 } from "gl-matrix"
import { NeuralColorMatchResult } from "./neural-color-match"
import { Sentry } from "@/telemetry/sentry"
import { AsShotWB, ColorSpaceCpu, renderCpu, RenderOptions } from "./renderer-cpu"
import { createGrayBand, getColorAndWeight } from "./lut-utils"
import { rgbToSat } from "./color-space"
import { GrayColorMean } from "./color-grading"

const enableColorMatchLog = false

export function logColorMatch(...info: any) {
	if (enableColorMatchLog) {
		console.log("[colorMatch]", ...info)
	}
}

export interface ColorStats {
	avgSaturation: number
	maxSaturation: number
	percentage: number
}

export type HueColor =
	| "red"
	| "orange"
	| "yellow"
	| "green"
	| "aqua"
	| "blue"
	| "purple"
	| "magenta"
	| "gray"

export interface ImageSatLumStats {
	totalAvgSat: number
	totalAvgLum: number
	lumPercentiles: number[]
	hueStats: Record<HueColor, ColorStats>
}

export interface ReferenceColorMatchFeatures {
	facePercent: number
	avgFaceHsvl: vec4 | null
	satLumStats: ImageSatLumStats
	vegetationHsv: vec3 | null
	colorPatches?: vec4[]
	whiteColor?: vec3 | null
	grayColor?: GrayColorMean | null
	imageId: string
	adjustmentsDigest: string
	strength?: number // Deprecated
	whiteBalance?: {
		asShotTemperature: number
		asShotTint: number
		temperature: number
		tint: number
	}
	presetIntensity?: number
	averageAdjustments?: Partial<Adjustments>
}

export interface ColorMatchRuntimeReferenceInfo {
	referenceId: string
	blendWeight: number
	runtimeDistance?: number
	colorStrength: number
	lightStrength: number
}

export interface ColorMatchRuntimeInfo {
	algorithm: "single" | "multi"
	references: ColorMatchRuntimeReferenceInfo[]
}

export interface InputColorMatchFeatures {
	facePercent: number
	avgFaceHsvl: vec4 | null
	colorPatches: vec4[]
	maxExposure?: number
	lumPercentiles?: number[]
	isRefKeyPhoto?: boolean
}

export interface InputColorMatchFeaturesWithFaceData extends InputColorMatchFeatures {
	faceColors: Float32Array | null
	faceWeights: Float32Array | null
}

export interface ReferenceItem {
	referenceId: string
	webpData256x256?: Uint8Array
	referenceFeatures: ReferenceColorMatchFeatures
	featureDist: number
	imageWbDist: number
	colorStrength: number
	luts?: NeuralColorMatchResult
}

export type LightingLimits = {
	minShadows: number
	maxShadows: number
	minBlacks: number
	maxBlacks: number
	minHighlights: number
	maxHighlights: number
	minWhites: number
	maxWhites: number
	minContrast: number
	maxContrast: number
}

export function getLightingLimits(options: {
	baseAdjustments?: Partial<Adjustments>
	limit?: number
}): LightingLimits {
	const { baseAdjustments, limit = 0.8 } = options
	const limitSmall = 0.3

	const minShadows = Math.min(baseAdjustments?.shadows ?? 0, -limitSmall)
	const maxShadows = Math.max(baseAdjustments?.shadows ?? 0, limit)
	const minBlacks = Math.min(baseAdjustments?.blacks ?? 0, -limitSmall)
	const maxBlacks = Math.max(baseAdjustments?.blacks ?? 0, limit)
	const minHighlights = Math.min(baseAdjustments?.highlights ?? 0, -limit)
	const maxHighlights = Math.max(baseAdjustments?.highlights ?? 0, limitSmall)
	const minWhites = Math.min(baseAdjustments?.whites ?? 0, -limit)
	const maxWhites = Math.max(baseAdjustments?.whites ?? 0, limitSmall)
	const minContrast = Math.min(baseAdjustments?.contrast ?? 0, -limit * 0.8)
	const maxContrast = Math.max(baseAdjustments?.contrast ?? 0, limit * 0.8)

	return {
		minShadows,
		maxShadows,
		minBlacks,
		maxBlacks,
		minHighlights,
		maxHighlights,
		minWhites,
		maxWhites,
		minContrast,
		maxContrast,
	}
}

export function wrapHueDiff(hueDiff: number) {
	if (hueDiff > 0.5) return hueDiff - 1
	if (hueDiff < -0.5) return hueDiff + 1
	return hueDiff
}

export function addHue(hue: number, delta: number) {
	hue += delta
	if (hue > 1) hue -= 1
	if (hue < 0) hue += 1
	return hue
}

export function wrapHueToLarge(hue: number) {
	if (hue < 0.5) return hue + 1
	return hue
}

export function wrapHueToSmall(hue: number) {
	if (hue > 0.5) return hue - 1
	return hue
}

export function getMeanDifference(
	arr1: Float32Array,
	arr2: Float32Array,
	start: number,
	end: number,
): number {
	let sum = 0
	for (let i = start; i < end; i++) {
		sum += (arr1[i] ?? 0) - (arr2[i] ?? 0)
	}
	return sum / (end - start)
}

export function getMeanAbsDifference(arr1: Float32Array, arr2: Float32Array): number {
	let sum = 0
	for (let i = 0; i < arr1.length; i++) {
		sum += Math.abs((arr1[i] ?? 0) - (arr2[i] ?? 0))
	}
	return sum / arr1.length
}

export function getGrayImage(image: Float32Array): Float32Array {
	const pixelCount = image.length / 3
	const result = new Float32Array(pixelCount)

	for (let i = 0; i < pixelCount; i++) {
		const r = image[i * 3] as number
		const g = image[i * 3 + 1] as number
		const b = image[i * 3 + 2] as number
		const gray = (r + g + b) / 3

		result[i] = gray
	}

	return result
}

export function normalizeToFloat32Array(pixels: Uint8Array): Float32Array {
	const result = new Float32Array(pixels.length)
	for (let i = 0; i < pixels.length; i++) {
		result[i] = (pixels[i] ?? 0) / 255.0
	}
	return result
}

export function capitalize(str: string) {
	return str.charAt(0).toUpperCase() + str.slice(1)
}

export function getWeightedDiff(
	current: Float32Array,
	target: Float32Array,
	weights: number[] | Float32Array,
): number {
	let weightedDiff = 0
	const pixelCount = current.length / 3

	for (let i = 0; i < pixelCount; i++) {
		const weight = weights[i] ?? 0
		const rDiff = Math.abs((current[i * 3] ?? 0) - (target[i * 3] ?? 0))
		const gDiff = Math.abs((current[i * 3 + 1] ?? 0) - (target[i * 3 + 1] ?? 0))
		const bDiff = Math.abs((current[i * 3 + 2] ?? 0) - (target[i * 3 + 2] ?? 0))
		weightedDiff += ((rDiff + gDiff + bDiff) / 3) * weight
	}

	return weightedDiff
}

/**
 * Calculates weighted difference between color tones while ignoring exposure differences
 * @param current Current RGB color values
 * @param target Target RGB color values
 * @param weights Weights for each pixel
 * @returns Weighted difference score focusing on color tone
 */
export function getWeightedToneDiff(
	current: Float32Array,
	target: Float32Array,
	weights: number[] | Float32Array,
): number {
	let weightedDiff = 0
	let totalWeight = 0
	const pixelCount = current.length / 3

	for (let i = 0; i < pixelCount; i++) {
		const weight = weights[i] ?? 0
		if (weight <= 0) continue

		totalWeight += weight

		// Get RGB values for current pixel
		const cR = current[i * 3] ?? 0
		const cG = current[i * 3 + 1] ?? 0
		const cB = current[i * 3 + 2] ?? 0

		// Get RGB values for target pixel
		const tR = target[i * 3] ?? 0
		const tG = target[i * 3 + 1] ?? 0
		const tB = target[i * 3 + 2] ?? 0

		const toneDiff = getToneDiff([cR, cG, cB], [tR, tG, tB])
		if (toneDiff < 0) continue

		// Compute weighted difference
		weightedDiff += toneDiff * weight
	}

	// Return average weighted difference
	return totalWeight > 0 ? weightedDiff / totalWeight : 0
}

export function getToneDiff(rgb1: vec3, rgb2: vec3) {
	const [cR, cG, cB] = rgb1
	const [tR, tG, tB] = rgb2

	// Calculate luminance for normalization
	const cLum = 0.2126 * cR + 0.7152 * cG + 0.0722 * cB
	const tLum = 0.2126 * tR + 0.7152 * tG + 0.0722 * tB

	// Skip pixels that are too dark (avoid division by near-zero)
	if (cLum < 0.01 || tLum < 0.01) return -1

	// Normalize RGB values to remove exposure differences
	// This preserves color tone while neutralizing brightness
	const cRNorm = cR / cLum
	const cGNorm = cG / cLum
	const cBNorm = cB / cLum

	const tRNorm = tR / tLum
	const tGNorm = tG / tLum
	const tBNorm = tB / tLum

	// Calculate difference in normalized space
	const rDiff = Math.abs(cRNorm - tRNorm)
	const gDiff = Math.abs(cGNorm - tGNorm)
	const bDiff = Math.abs(cBNorm - tBNorm)

	return (rDiff + gDiff + bDiff) / 3
}

// export function getWeightedHSVDiff(
// 	current: Float32Array,
// 	target: Float32Array,
// 	weights: number[],
// ): number {
// 	let weightedDiff = 0
// 	const pixelCount = current.length / 3

// 	for (let i = 0; i < pixelCount; i++) {
// 		const weight = weights[i] ?? 0
// 		const hueDiff = Math.abs(wrapHueDiff((current[i * 3] ?? 0) - (target[i * 3] ?? 0)))
// 		const satDiff = Math.abs((current[i * 3 + 1] ?? 0) - (target[i * 3 + 1] ?? 0))
// 		const lumDiff = Math.abs((current[i * 3 + 2] ?? 0) - (target[i * 3 + 2] ?? 0))
// 		weightedDiff += ((hueDiff + satDiff + lumDiff) / 3) * weight
// 	}

// 	return weightedDiff
// }

export function getWeightedGrayDiff(
	current: Float32Array,
	target: Float32Array,
	weights: number[],
): number {
	let weightedDiff = 0
	const pixelCount = current.length / 3

	if (current.length !== target.length || pixelCount !== weights.length) {
		console.error("Current and target arrays must have the same length")
		return 0
	}

	for (let i = 0; i < pixelCount; i++) {
		const weight = weights[i] ?? 0
		const meanCurrent =
			((current[i * 3] ?? 0) + (current[i * 3 + 1] ?? 0) + (current[i * 3 + 2] ?? 0)) / 3
		const meanTarget =
			((target[i * 3] ?? 0) + (target[i * 3 + 1] ?? 0) + (target[i * 3 + 2] ?? 0)) / 3
		const diff = Math.abs(meanCurrent - meanTarget)
		weightedDiff += diff * weight
	}

	return weightedDiff
}

export function getWeightedMean(
	data: Float32Array,
	normalizedWeights: number[] | Float32Array,
): number {
	if (data.length !== normalizedWeights.length) {
		throw new Error("Data and weights must have the same length")
	}

	let total = 0
	for (let i = 0; i < data.length; i++) {
		total += (data[i] ?? 0) * (normalizedWeights[i] ?? 0)
	}
	return total
}

export function gatherRGBValuesInt(array: Uint8Array, indices: Float32Array): Uint8Array {
	const result = new Uint8Array(indices.length * 3)
	for (let i = 0; i < indices.length; i++) {
		const baseIndex = (indices[i] as number) * 3
		result[i * 3] = array[baseIndex] ?? 0
		result[i * 3 + 1] = array[baseIndex + 1] ?? 0
		result[i * 3 + 2] = array[baseIndex + 2] ?? 0
	}
	return result
}

export function gatherRGBValues(array: Float32Array, indices: number[]): Float32Array {
	const result = new Float32Array(indices.length * 3)
	for (let i = 0; i < indices.length; i++) {
		const baseIndex = (indices[i] as number) * 3
		result[i * 3] = array[baseIndex] ?? 0
		result[i * 3 + 1] = array[baseIndex + 1] ?? 0
		result[i * 3 + 2] = array[baseIndex + 2] ?? 0
	}
	return result
}

export function gatherValues(array: Float32Array | number[], indices: number[]): Float32Array {
	const result = new Float32Array(indices.length)
	for (let i = 0; i < indices.length; i++) {
		result[i] = array[indices[i] as number] ?? 0
	}
	return result
}

export function getMeanRGB(rgbData: Uint8Array | Float32Array): vec3 {
	const meanRgb: vec3 = [0, 0, 0]
	for (let i = 0; i < rgbData.length; i += 3) {
		meanRgb[0] += rgbData[i] ?? 0
		meanRgb[1] += rgbData[i + 1] ?? 0
		meanRgb[2] += rgbData[i + 2] ?? 0
	}
	return meanRgb.map((c) => c / (rgbData.length / 3)) as vec3
}

export function getLuminance(rgbData: Uint8Array | Float32Array): number[] {
	const luminances: number[] = []

	for (let i = 0; i < rgbData.length; i += 3) {
		const r = rgbData[i] ?? 0
		const g = rgbData[i + 1] ?? 0
		const b = rgbData[i + 2] ?? 0

		const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
		luminances.push(luminance)
	}

	return luminances
}

export function getMedianLuminance(rgbData: Uint8Array | Float32Array): number {
	// Calculate luminance for each pixel using the standard formula:
	// Y = 0.2126*R + 0.7152*G + 0.0722*B
	const luminances = getLuminance(rgbData)

	// Sort luminances to find median
	luminances.sort((a, b) => a - b)

	// Find the median value
	const mid = clamp(Math.floor(luminances.length / 2), 0, luminances.length - 1)

	return luminances[mid] ?? 0.5
}

export function getWeightedMedianLuminance(
	rgbData: Float32Array,
	weights: number[] | Float32Array,
): number {
	const percentiles = getLuminancePercentiles(rgbData, weights, [0.5])
	return percentiles[0] ?? 0.5
}

export function getWeightedMeanRgb(
	image: Float32Array,
	pixelWeights: Float32Array,
	minPixelWeight: number = 0.0005,
): vec3 | null {
	if (image.length === 0) {
		return null
	}
	const meanRgb: vec3 = [0, 0, 0]
	let totalWeight = 0
	for (let i = 0; i < pixelWeights.length; i += 1) {
		const weight = pixelWeights[i] ?? 0
		totalWeight += weight
		meanRgb[0] += (image[i * 3] ?? 0) * weight
		meanRgb[1] += (image[i * 3 + 1] ?? 0) * weight
		meanRgb[2] += (image[i * 3 + 2] ?? 0) * weight
	}
	if (totalWeight < minPixelWeight) {
		return null
	}
	return meanRgb.map((c) => c / totalWeight) as vec3
}

export function getRGBDataWithStep(
	imageData: number[], // should be 4 channels and Uint8Array
	targetSize: number = 2000,
	darkThreshold = 0.1,
	brightThreshold = 0.1,
): { faceRgbPixels: Uint8Array; startIndex: number; endIndex: number } {
	const originalSize = imageData.length / 4
	const step = Math.max(1, Math.round(originalSize / targetSize))
	const newSize = Math.floor(originalSize / step)
	const result = new Uint8Array(newSize * 3)
	const luminances = new Float32Array(newSize)
	for (let i = 0; i < newSize; i++) {
		const k = i * step
		result[i * 3] = imageData[k * 4] ?? 0
		result[i * 3 + 1] = imageData[k * 4 + 1] ?? 0
		result[i * 3 + 2] = imageData[k * 4 + 2] ?? 0
		luminances[i] =
			((result[i * 3] ?? 0) + (result[i * 3 + 1] ?? 0) + (result[i * 3 + 2] ?? 0)) / 3.0
	}

	// Sort pixels by luminance and determine cutoff indices
	const sortedIndices = luminances
		.map((_, i) => i)
		.sort((a, b) => (luminances[a] ?? 0) - (luminances[b] ?? 0))

	let startIndex = Math.floor(sortedIndices.length * darkThreshold)
	const endIndex = Math.floor(sortedIndices.length * (1 - brightThreshold))
	const normalLumIndex = Math.floor(sortedIndices.length * 0.65)
	function getLuminance(index: number) {
		return luminances[sortedIndices[index] ?? 0] ?? 0
	}
	const normalLum = getLuminance(normalLumIndex)
	logColorMatch("[getFaceRGBDataWithStep] startIndex", {
		startIndex,
		startLum: getLuminance(startIndex),
		endLum: getLuminance(endIndex),
		normalLum,
	})

	// filter out dark pixels if brighter pixels exist (remove very dark face when bright face exists)
	const lumThreshold = Math.max(Math.min(normalLum - 70, 40), Math.min(normalLum - 25, 20))
	const maxIndex = Math.min(Math.floor(sortedIndices.length * 0.4), endIndex - 1000)
	while (startIndex < maxIndex && getLuminance(startIndex) < lumThreshold) {
		startIndex += Math.floor(sortedIndices.length * 0.01)
	}
	logColorMatch("[getFaceRGBDataWithStep] startIndex, endIndex", startIndex, endIndex)

	// Filter pixels within the luminance range
	const filteredIndices = sortedIndices.slice(startIndex, endIndex)

	const filteredResult = gatherRGBValuesInt(result, filteredIndices)

	return { faceRgbPixels: filteredResult, startIndex, endIndex }
}

export function getRGBDataWithStartEndIndex(
	imageData: number[], // should be 4 channels and Uint8Array
	targetSize: number = 2000,
	startIndex: number,
	endIndex: number,
): Uint8Array {
	const originalSize = imageData.length / 4
	const step = Math.max(1, Math.round(originalSize / targetSize))
	const newSize = Math.floor(originalSize / step)
	const result = new Uint8Array(newSize * 3)
	const luminances = new Float32Array(newSize)
	for (let i = 0; i < newSize; i++) {
		const k = i * step
		result[i * 3] = imageData[k * 4] ?? 0
		result[i * 3 + 1] = imageData[k * 4 + 1] ?? 0
		result[i * 3 + 2] = imageData[k * 4 + 2] ?? 0
		luminances[i] =
			((result[i * 3] ?? 0) + (result[i * 3 + 1] ?? 0) + (result[i * 3 + 2] ?? 0)) / 3.0
	}

	// Sort pixels by luminance and determine cutoff indices
	const sortedIndices = luminances
		.map((_, i) => i)
		.sort((a, b) => (luminances[a] ?? 0) - (luminances[b] ?? 0))

	// Filter pixels within the luminance range
	const filteredIndices = sortedIndices.slice(startIndex, endIndex)

	const filteredResult = gatherRGBValuesInt(result, filteredIndices)

	return filteredResult
}

export function getDecreasedValue(value: number, limit: number, scale: number) {
	if (Math.abs(value) > limit) {
		value = ((Math.abs(value) - limit) * scale + limit) * (value > 0 ? 1 : -1)
	}
	return value
}

export function step(x: number, edge1: number, edge2: number) {
	if (x < edge1) return 0
	if (x > edge2) return 1
	return (x - edge1) / (edge2 - edge1)
}

export function mix(a: number, b: number, t: number) {
	return a * (1 - t) + b * t
}

/**
 * Calculates weighted luminance percentiles from RGB color data
 * @param colors RGB color data as Float32Array (R,G,B triplets)
 * @param pixelWeights Weight for each pixel
 * @param percentiles Array of percentiles to calculate (0.0 to 1.0)
 * @returns Object mapping each percentile to its luminance value
 */
export function getLuminancePercentiles(
	colors: Float32Array,
	pixelWeights: number[] | Float32Array,
	percentiles: number[] = [0.75, 0.9, 0.95, 0.99],
	luma: boolean = true,
): number[] {
	// Ensure colors array has complete RGB triplets
	if (colors.length % 3 !== 0) {
		throw new Error("Colors array length must be a multiple of 3")
	}

	// Ensure we have the same number of weights as pixels
	if (pixelWeights.length !== colors.length / 3) {
		throw new Error("Number of weights must match number of pixels")
	}

	// Calculate luminance for each pixel and pair with its weight
	const luminanceWeightPairs: Array<[number, number]> = []
	let totalWeight = 0

	for (let i = 0; i < colors.length; i += 3) {
		const r = colors[i] as number
		const g = colors[i + 1] as number
		const b = colors[i + 2] as number

		// Calculate luminance using standard formula
		const luminance = luma
			? 0.2126 * r + 0.7152 * g + 0.0722 * b
			: (Math.max(r, g, b) + Math.min(r, g, b)) / 2

		// Get the weight for this pixel
		const weight = pixelWeights[i / 3] as number
		totalWeight += weight

		// Store the pair
		luminanceWeightPairs.push([luminance, weight])
	}

	// Sort pairs by luminance (ascending)
	luminanceWeightPairs.sort((a, b) => a[0] - b[0])

	// Calculate cumulative weights
	const cumulativeWeights: number[] = []
	let cumulativeWeight = 0

	for (const [_, weight] of luminanceWeightPairs) {
		cumulativeWeight += weight
		cumulativeWeights.push(cumulativeWeight)
	}

	// Normalize cumulative weights
	for (let i = 0; i < cumulativeWeights.length; i++) {
		cumulativeWeights[i] = (cumulativeWeights[i] ?? 0) / totalWeight
	}

	// Find luminance at each percentile
	const result: number[] = []

	for (const percentile of percentiles) {
		// Find the index where cumulative weight exceeds the percentile
		let index = cumulativeWeights.findIndex((w) => w >= percentile)

		// If not found (should not happen if percentiles are valid), use the last element
		if (index === -1) {
			index = luminanceWeightPairs.length - 1
		}

		// Get the luminance at this index
		result.push(luminanceWeightPairs[index]?.[0] ?? 0)
	}

	return result
}

export function getLightnessPercentiles(
	lightness: Float32Array,
	pixelWeights: number[] | Float32Array,
	percentiles: number[],
): number[] {
	// Ensure we have the same number of weights as pixels
	if (pixelWeights.length !== lightness.length) {
		throw new Error("Number of weights must match number of pixels")
	}
	if (lightness.length === 0) {
		throw new Error("[getLightnessPercentiles] Input array is empty")
	}

	// Calculate luminance for each pixel and pair with its weight
	const luminanceWeightPairs: Array<[number, number]> = []
	let totalWeight = 0

	for (let i = 0; i < lightness.length; i++) {
		const luminance = lightness[i] as number

		// Get the weight for this pixel
		const weight = pixelWeights[i] as number
		totalWeight += weight

		// Store the pair
		luminanceWeightPairs.push([luminance, weight])
	}

	// Sort pairs by luminance (ascending)
	luminanceWeightPairs.sort((a, b) => a[0] - b[0])

	// Calculate cumulative weights
	const cumulativeWeights: number[] = []
	let cumulativeWeight = 0

	for (const [_, weight] of luminanceWeightPairs) {
		cumulativeWeight += weight
		cumulativeWeights.push(cumulativeWeight)
	}

	// Normalize cumulative weights
	for (let i = 0; i < cumulativeWeights.length; i++) {
		cumulativeWeights[i] = (cumulativeWeights[i] ?? 0) / totalWeight
	}

	// Find luminance at each percentile
	const result: number[] = []

	for (const percentile of percentiles) {
		// Find the index where cumulative weight exceeds the percentile
		let index = cumulativeWeights.findIndex((w) => w >= percentile)

		// If not found (should not happen if percentiles are valid), use the last element
		if (index === -1) {
			index = luminanceWeightPairs.length - 1
		}

		// Get the luminance at this index
		result.push(luminanceWeightPairs[index]?.[0] ?? 0)
	}

	return result
}

export function getAutoHighlights(colors: Float32Array, pixelWeights: number[]) {
	const percentiles = getLuminancePercentiles(colors, pixelWeights)
	return getAutoHighlightsFromPercentiles(percentiles)
}

export function getAutoHighlightsFromPercentiles(percentiles: number[]) {
	const n = percentiles.length
	const percentile75 = percentiles[n - 4] ?? 0
	const percentile90 = percentiles[n - 3] ?? 0
	const percentile95 = percentiles[n - 2] ?? 0
	const percentile99 = percentiles[n - 1] ?? 0
	const highlights =
		Math.min(0.99 - percentile99, 0) +
		Math.min(0.94 - percentile95, 0) +
		Math.min(0.89 - percentile90, 0) +
		Math.min(0.85 - percentile75, 0) * 0.8

	// logColorMatch("[getAutoHighlights] info", { percentile75, percentile90, percentile95, percentile99 })
	return highlights * 1.7
}

export function getReferenceCacheId({
	referenceImageId,
	adjustmentsDigest,
	adjustments,
}: {
	referenceImageId: string
	adjustmentsDigest?: string
	adjustments?: Adjustments
}) {
	const adjustmentsDigestFinal = adjustmentsDigest ?? hashObject(adjustments ?? {})
	return referenceImageId + "|" + adjustmentsDigestFinal
}

/**
 * Returns true if every component of `values` is within ±eps of zero.
 * Used to detect degenerate inputs to color-match WB / chromatic-adaptation
 * math that would otherwise propagate NaN through `normalize_xyz`.
 */
export function isAllNearZero(values: ArrayLike<number>, eps = 1e-3): boolean {
	for (let i = 0; i < values.length; i++) {
		if (Math.abs(values[i] ?? 0) > eps) return false
	}
	return true
}

/**
 * Sanity check the three photo color stages flowing into `estimateParameters`:
 *   - `inputBefore`: base-rendered photo colors (output of `baseAdjustments`)
 *   - `inputWithPreset`: photo colors after the photo's preset / profile is
 *     applied (this is what feeds into the neural LUT)
 *   - `inputAfter`: post-LUT colors (LUT output, weighted across refs in multi)
 *
 * If any stage is all-near-zero, the upstream rendering or neural LUT produced
 * a degenerate frame; downstream WB / chromatic-adaptation math will likely
 * NaN. We don't bail (boundary guards in `getWhiteBlanceParams` /
 * `iterateWhiteBalance` handle that safely), we just publish a Sentry signal
 * with which stage failed so the root cause can be pinpointed.
 */
export function reportDegenerateColorMatchInputs(args: {
	algorithm: "single" | "multi"
	inputBefore: Float32Array
	inputWithPreset: Float32Array
	inputAfter: Float32Array
	asShotWB: AsShotWB
	hasBaseAdjustments: boolean
	computeWb: boolean
}): void {
	const inputBeforeAllZero = isAllNearZero(args.inputBefore)
	const inputWithPresetAllZero = isAllNearZero(args.inputWithPreset)
	const inputAfterAllZero = isAllNearZero(args.inputAfter)
	if (!inputBeforeAllZero && !inputWithPresetAllZero && !inputAfterAllZero) {
		return
	}
	Sentry.captureMessage(
		"Color match got all-near-zero photo colors; rendering/LUT may be corrupted",
		{
			level: "warning",
			extra: {
				algorithm: args.algorithm,
				inputBeforeAllZero,
				inputWithPresetAllZero,
				inputAfterAllZero,
				inputBeforeSample: Array.from(args.inputBefore.slice(0, 9)),
				inputWithPresetSample: Array.from(args.inputWithPreset.slice(0, 9)),
				inputAfterSample: Array.from(args.inputAfter.slice(0, 9)),
				inputLength: args.inputBefore.length,
				asShotWB: args.asShotWB,
				hasBaseAdjustments: args.hasBaseAdjustments,
				computeWb: args.computeWb,
			},
		},
	)
}

export function ensureValidFaceData(
	inputFeatures: InputColorMatchFeaturesWithFaceData,
	referenceFeatures?: ReferenceColorMatchFeatures,
) {
	if (
		inputFeatures.avgFaceHsvl &&
		(inputFeatures.avgFaceHsvl.some((v) => isNaN(v)) ||
			!inputFeatures.faceColors?.length ||
			!inputFeatures.faceWeights?.length)
	) {
		console.error("[Color Match] NaN in input avgFaceHsvl or missing face data", inputFeatures)
		Sentry.captureException(
			new Error("[Color Match] NaN in input avgFaceHsvl or missing face data"),
			{
				extra: {
					inputFeatures,
				},
			},
		)

		inputFeatures.avgFaceHsvl = null
		inputFeatures.facePercent = 0
		inputFeatures.faceColors = null
		inputFeatures.faceWeights = null
	}

	if (
		referenceFeatures &&
		referenceFeatures.avgFaceHsvl &&
		referenceFeatures.avgFaceHsvl.some((v) => isNaN(v))
	) {
		console.error("[Color Match] NaN in reference avgFaceHsvl", referenceFeatures)
		Sentry.captureException(new Error("[Color Match] NaN in reference avgFaceHsvl"), {
			extra: {
				referenceFeatures,
			},
		})
		referenceFeatures.avgFaceHsvl = null
		referenceFeatures.facePercent = 0
	}
}

export function getInputColorsAndWeights(options: {
	inputFeatures: InputColorMatchFeaturesWithFaceData
	baseAdjustments?: Partial<Adjustments>
	inputImageBitmap: ImageBitmap
	baseRenderOptions: RenderOptions
}) {
	const { inputFeatures, baseAdjustments, inputImageBitmap, baseRenderOptions } = options

	// face data
	const faceBefore = inputFeatures.faceColors
	const faceWeights = inputFeatures.faceWeights
	let faceColorsWithPreset = faceBefore
	if (baseAdjustments && faceBefore) {
		faceColorsWithPreset = renderCpu(faceBefore, baseAdjustments, {
			inputColorSpace: ColorSpaceCpu.sRGB,
			outColorSpace: ColorSpaceCpu.sRGB,
			...baseRenderOptions,
		})
	}

	// extract colors and weights from input image
	const { colors: inputBefore, pixelWeights } = getColorAndWeight({
		inputImageBitmap,
		faceColors: inputFeatures.faceColors,
		faceWeights: inputFeatures.faceWeights,
	})
	let colorsWithPreset = inputBefore
	if (baseAdjustments) {
		colorsWithPreset = renderCpu(inputBefore, baseAdjustments, {
			inputColorSpace: ColorSpaceCpu.sRGB,
			outColorSpace: ColorSpaceCpu.sRGB,
			...baseRenderOptions,
		})
	}

	// Create a test pattern in sRGB in range [0, 1]
	const grayBandBefore = createGrayBand(256)

	let grayWithPreset = grayBandBefore
	if (baseAdjustments) {
		grayWithPreset = renderCpu(grayBandBefore, baseAdjustments, {
			inputColorSpace: ColorSpaceCpu.sRGB,
			outColorSpace: ColorSpaceCpu.sRGB,
			...baseRenderOptions,
		})
	}

	return {
		faceBefore,
		faceColorsWithPreset,
		faceWeights,
		inputBefore,
		colorsWithPreset,
		pixelWeights,
		grayBandBefore,
		grayWithPreset,
	}
}

export function getWeightedColors(inputAfters: Float32Array[], weights: number[]) {
	const N = inputAfters[0]?.length ?? 0
	const inputAfter = new Float32Array(N)
	for (let i = 0; i < inputAfters.length; i++) {
		for (let j = 0; j < N; j++) {
			inputAfter[j] = (inputAfter[j] ?? 0) + (inputAfters[i]?.[j] ?? 0) * (weights[i] ?? 0)
		}
	}
	return inputAfter
}

function getWhiteRegionWeights(whiteColors: (vec3 | null)[], weights: number[], wbDist: number[]) {
	// Calculate white region weights based on neutrality (lowest saturation)
	const whiteRegionWeights: number[] = []
	const whiteScores: number[] = []

	// First pass: calculate scores for all white regions, lower saturation and less blue-green is better (natural white)
	for (let i = 0; i < weights.length; i++) {
		const whiteColor = whiteColors[i]
		if (whiteColor) {
			const [r, g, b] = whiteColor
			const score =
				rgbToSat([r, g, b]) +
				Math.max(0, b - (r + g) * 0.5) * 0.25 +
				Math.max(0, g - (r + b) * 0.5) * 0.25
			whiteScores.push(score)
		} else {
			whiteScores.push(Infinity) // Mark as invalid
		}
	}

	// Find minimum saturation (most neutral white)
	const validScores = whiteScores.filter((s) => s !== Infinity)
	const minScore = validScores.length > 0 ? Math.min(...validScores) : 0

	const minWbDist = Math.max(
		0.001,
		wbDist.reduce((min, dist) => Math.min(min, dist), Infinity),
	)

	// Second pass: calculate weights relative to minimum saturation
	for (let i = 0; i < weights.length; i++) {
		const whiteColor = whiteColors[i]
		const score = whiteScores[i]
		if (whiteColor && score !== Infinity && score !== undefined) {
			// Scale weight based on how much more saturated this is compared to the minimum
			const scoreRatio =
				minScore === 0 ? 1 : Math.max(0.1, minScore / Math.max(score, minScore))
			const wbRatio =
				minWbDist === 0
					? 1
					: Math.max(0.1, minWbDist / Math.max(wbDist[i] ?? minWbDist, minWbDist))
			whiteRegionWeights.push(Math.min(scoreRatio, wbRatio * 0.5 + scoreRatio * 0.5))
		} else {
			whiteRegionWeights.push(0)
		}
	}
	logColorMatch("[getWeightedRefFeatures] whiteRegionWeights", {
		whiteScores,
		whiteRegionWeights,
	})
	return whiteRegionWeights
}

export function getWeightedRefFeatures(
	referenceFeatures: ReferenceColorMatchFeatures[],
	weights: number[],
	inputAvgFaceHsvl: vec4 | null,
	wbDist: number[],
) {
	let facePercent = 0
	const avgFaceHsvl = vec4.create()
	let satLumStats = {} as ImageSatLumStats
	const vegetationHsv = vec3.create()
	let asShotTemperature = 0
	let asShotTint = 0
	let temperature = 0
	let tint = 0
	const whiteColor = vec3.create()
	const grayColorGlobal = vec3.create() // Note here we only use global gray color

	let totalWeightFace = 0
	let totalWeightVegetation = 0
	let totalWeightWhiteBalance = 0
	let totalWeightWhiteRegion = 0
	let totalWeightGrayRegion = 0

	const whiteRegionWeights = getWhiteRegionWeights(
		referenceFeatures.map((r) => r.whiteColor ?? null),
		weights,
		wbDist,
	)
	const grayRegionWeights = getWhiteRegionWeights(
		referenceFeatures.map((r) => r.grayColor?.global ?? null),
		weights,
		wbDist,
	)

	// Weight faces based on similarity to input avgFaceHsvl
	const weightFaces = new Float32Array(weights.length)
	const distFaces = new Float32Array(weights.length)
	for (let i = 0; i < weights.length; i++) {
		const refFaceHsvl = referenceFeatures[i]?.avgFaceHsvl
		if (refFaceHsvl && inputAvgFaceHsvl) {
			const hueDiff = Math.abs(refFaceHsvl[0] - inputAvgFaceHsvl[0]) * 3.6
			const satDiff = Math.abs(refFaceHsvl[1] - inputAvgFaceHsvl[1])
			const lumDiff = Math.abs(refFaceHsvl[2] - inputAvgFaceHsvl[2])
			distFaces[i] = hueDiff * 3 + satDiff * 0.3 + lumDiff * 0.15
		} else {
			distFaces[i] = 1000 // invalid
		}
	}
	const minDistFace = Math.min(...distFaces)
	for (let i = 0; i < weights.length; i++) {
		const refFaceHsvl = referenceFeatures[i]?.avgFaceHsvl
		const dist = distFaces[i] ?? 1000
		if (refFaceHsvl && inputAvgFaceHsvl && dist < 1000) {
			const weightFace = clamp(
				(minDistFace * 0.5) / Math.max(dist - minDistFace * 0.5, 0.0001),
				0.1,
				1,
			)
			weightFaces[i] = weightFace
			totalWeightFace += weightFace
		} else if (refFaceHsvl) {
			weightFaces[i] = weights[i] ?? 0
			totalWeightFace += weightFaces[i] as number
		}
		if (referenceFeatures[i]?.vegetationHsv) {
			totalWeightVegetation += weights[i] ?? 0
		}
		if (referenceFeatures[i]?.whiteBalance) {
			totalWeightWhiteBalance += weights[i] ?? 0
		}
		if (referenceFeatures[i]?.whiteColor) {
			totalWeightWhiteRegion += whiteRegionWeights[i] ?? 0
		}
		if (referenceFeatures[i]?.grayColor?.global) {
			totalWeightGrayRegion += grayRegionWeights[i] ?? 0
		}
	}
	totalWeightFace = Math.max(totalWeightFace, 0.00001)
	totalWeightVegetation = Math.max(totalWeightVegetation, 0.00001)
	totalWeightWhiteBalance = Math.max(totalWeightWhiteBalance, 0.00001)
	totalWeightWhiteRegion = Math.max(totalWeightWhiteRegion, 0.00001)
	logColorMatch("[getWeightedRefFeatures] weightFaces", {
		distFaces,
		minDistFace,
		weightFaces,
		totalWeightFace,
	})

	// Initialize weighted satLumStats
	let totalAvgSat = 0
	let totalAvgLum = 0
	const lumPercentiles = [0, 0, 0, 0, 0, 0, 0] // percentiles length is 7
	const hueStats: Record<HueColor, ColorStats> = {
		red: { avgSaturation: 0, maxSaturation: 0, percentage: 0 },
		orange: { avgSaturation: 0, maxSaturation: 0, percentage: 0 },
		yellow: { avgSaturation: 0, maxSaturation: 0, percentage: 0 },
		green: { avgSaturation: 0, maxSaturation: 0, percentage: 0 },
		aqua: { avgSaturation: 0, maxSaturation: 0, percentage: 0 },
		blue: { avgSaturation: 0, maxSaturation: 0, percentage: 0 },
		purple: { avgSaturation: 0, maxSaturation: 0, percentage: 0 },
		magenta: { avgSaturation: 0, maxSaturation: 0, percentage: 0 },
		gray: { avgSaturation: 0, maxSaturation: 0, percentage: 0 },
	}

	// Calculate total weight for each hue separately
	const hueWeights: Record<HueColor, number> = {
		red: 0,
		orange: 0,
		yellow: 0,
		green: 0,
		aqua: 0,
		blue: 0,
		purple: 0,
		magenta: 0,
		gray: 0,
	}

	// First pass: calculate total weights for each hue
	for (let i = 0; i < weights.length; i++) {
		const refFeature = referenceFeatures[i]
		if (!refFeature) continue
		const weight = weights[i] ?? 0

		for (const hue of Object.keys(hueWeights) as HueColor[]) {
			if (
				refFeature.satLumStats.hueStats[hue] != null &&
				refFeature.satLumStats.hueStats[hue].percentage > 0
			) {
				hueWeights[hue] += weight
			}
		}
	}

	// Ensure no zero weights
	for (const hue of Object.keys(hueWeights) as HueColor[]) {
		hueWeights[hue] = Math.max(hueWeights[hue], 0.00001)
	}

	// Calculate total weight for overall stats
	let totalWeightSatLum = 0
	for (let i = 0; i < weights.length; i++) {
		if (referenceFeatures[i]?.satLumStats.lumPercentiles.length) {
			totalWeightSatLum += weights[i] ?? 0
		}
	}
	totalWeightSatLum = Math.max(totalWeightSatLum, 0.00001)

	for (let i = 0; i < weights.length; i++) {
		const refFeature = referenceFeatures[i]
		if (!refFeature) continue
		const w = weights[i] ?? 0
		const weightFace = (weightFaces[i] ?? 0) / totalWeightFace
		const weightVegetation = w / totalWeightVegetation
		const weightWhiteBalance = w / totalWeightWhiteBalance
		const weightSatLum = w / totalWeightSatLum
		const weightWhiteRegion = (whiteRegionWeights[i] ?? 0) / totalWeightWhiteRegion
		const weightGrayRegion = (grayRegionWeights[i] ?? 0) / totalWeightGrayRegion

		facePercent += refFeature.facePercent * weightFace
		if (refFeature.avgFaceHsvl) {
			vec4.scaleAndAdd(avgFaceHsvl, avgFaceHsvl, refFeature.avgFaceHsvl, weightFace)
		}

		// Weighted satLumStats computation
		totalAvgSat += refFeature.satLumStats.totalAvgSat * weightSatLum
		totalAvgLum += refFeature.satLumStats.totalAvgLum * weightSatLum

		// Weight luminance percentiles
		for (
			let p = 0;
			p < refFeature.satLumStats.lumPercentiles.length && p < lumPercentiles.length;
			p++
		) {
			lumPercentiles[p] =
				(lumPercentiles[p] ?? 0) +
				(refFeature.satLumStats.lumPercentiles[p] ?? 0) * weightSatLum
		}

		// Weight hue stats with normalized weights per hue
		for (const hue of Object.keys(hueStats) as HueColor[]) {
			const refHueStats = refFeature.satLumStats.hueStats[hue]
			if (refHueStats != null) {
				const normalizedHueWeight = w / hueWeights[hue]
				hueStats[hue].avgSaturation += refHueStats.avgSaturation * normalizedHueWeight
				hueStats[hue].maxSaturation += refHueStats.maxSaturation * normalizedHueWeight
				hueStats[hue].percentage += refHueStats.percentage * weightSatLum
			}
		}

		if (refFeature.vegetationHsv) {
			vec3.scaleAndAdd(
				vegetationHsv,
				vegetationHsv,
				refFeature.vegetationHsv,
				weightVegetation,
			)
		}
		if (refFeature.whiteBalance) {
			asShotTemperature += refFeature.whiteBalance.asShotTemperature * weightWhiteBalance
			asShotTint += refFeature.whiteBalance.asShotTint * weightWhiteBalance
			temperature += refFeature.whiteBalance.temperature * weightWhiteBalance
			tint += refFeature.whiteBalance.tint * weightWhiteBalance
		}
		if (refFeature.whiteColor) {
			vec3.scaleAndAdd(whiteColor, whiteColor, refFeature.whiteColor, weightWhiteRegion)
		}
		if (refFeature.grayColor?.global) {
			vec3.scaleAndAdd(
				grayColorGlobal,
				grayColorGlobal,
				refFeature.grayColor.global,
				weightGrayRegion,
			)
		}
	}

	satLumStats = {
		totalAvgSat,
		totalAvgLum,
		lumPercentiles,
		hueStats,
	}

	const whiteBalance = {
		asShotTemperature,
		asShotTint,
		temperature,
		tint,
	}

	const grayColor = { ...(referenceFeatures.find((r) => r.grayColor)?.grayColor ?? {}) }
	grayColor.global = totalWeightGrayRegion > 0.01 ? grayColorGlobal : null
	const averageAdjustments =
		referenceFeatures.find((r) => r.averageAdjustments)?.averageAdjustments ?? undefined
	const result = {
		facePercent,
		avgFaceHsvl: facePercent > 0 && avgFaceHsvl[0] > 0 ? avgFaceHsvl : null,
		satLumStats,
		vegetationHsv: totalWeightVegetation > 0.01 ? vegetationHsv : null,
		whiteColor: totalWeightWhiteRegion > 0.01 ? whiteColor : null,
		grayColor,
		whiteBalance: temperature == 0 || asShotTemperature == 0 ? null : whiteBalance,
		averageAdjustments,
	}
	logColorMatch("[multi-ref] referenceFeatures", { referenceFeatures, weights, result })

	return result as ReferenceColorMatchFeatures
}

export function isNearlyBW(refSatStats: ImageSatLumStats | undefined) {
	if (!refSatStats) return false
	const maxSatOfHues = Object.values(refSatStats.hueStats).reduce(
		(max, hue) => Math.max(max, hue.maxSaturation),
		0,
	)
	if (refSatStats.totalAvgSat < 0.03 || (refSatStats.totalAvgSat < 0.1 && maxSatOfHues < 0.2)) {
		return true
	}
	return false
}

// Address outlier reference that's not so similar to the input photo and has too strong WB adjustments
export function getClampedWBBias(options: {
	featureDist: number
	tempBias: number
	tintBias: number
}) {
	const { featureDist, tempBias, tintBias } = options
	let tempBiasFinal = tempBias
	let tintBiasFinal = tintBias
	if (featureDist > 0.2) {
		const tempBiasClamped = clamp(tempBias, -0.1, 0.1)
		const tintBiasClamped = clamp(tintBias, -10, 10)
		const factor = clamp((0.45 - featureDist) * 4, 0, 1)
		tempBiasFinal = tempBiasClamped + (tempBias - tempBiasClamped) * factor
		tintBiasFinal = tintBiasClamped + (tintBias - tintBiasClamped) * factor
	}
	return { tempBiasFinal, tintBiasFinal }
}

export function isAnyNaNorInf(
	value: Float32Array | number[] | number,
): "Valid" | "NaN" | "Inf" | "NaN_and_Inf" {
	let hasNaN = false
	let hasInf = false

	const checkNumber = (v: number) => {
		if (Number.isNaN(v)) {
			hasNaN = true
		} else if (!Number.isFinite(v)) {
			hasInf = true
		}
	}

	if (Array.isArray(value)) {
		for (const v of value) {
			checkNumber(v)
			if (hasNaN && hasInf) break
		}
	} else if (ArrayBuffer.isView(value)) {
		for (const v of value) {
			checkNumber(v)
			if (hasNaN && hasInf) break
		}
	} else if (typeof value === "number") {
		checkNumber(value)
	}

	if (hasNaN && hasInf) return "NaN_and_Inf"
	if (hasNaN) return "NaN"
	if (hasInf) return "Inf"
	return "Valid"
}
